<template>
    <v-container>
        <div class="limiter-content pa-2 pa-lg-0">
            <v-row align="center" class="mb-4">
                <v-col>
                    <div class="font-weight-bold mt-10 title-page" tabindex="0">Minhas Impugnações</div>
                    <p class="text-subtitle-1 grey--text mb-0">
                        Acompanhe a situação das impugnações vinculadas ao seu cadastro.
                    </p>
                </v-col>
                <v-col cols="auto">
                    <v-btn outlined color="primary" to="/home">
                        <v-icon left>mdi-arrow-left</v-icon> Voltar
                    </v-btn>
                </v-col>
            </v-row>

            <v-row v-if="dataset.loading" justify="center" class="my-10">
                <v-progress-circular indeterminate color="primary" size="56" />
            </v-row>

            <template v-else>
                <v-card v-if="certificateFeatureEnabled && $modules.isVisible('site.credit_certificate.issue')" outlined class="mb-4">
                    <ModuleClosedAlert module="site.credit_certificate.issue" alert-class="mx-3 mt-3 mb-0" />
                    <v-card-text>
                        <div class="d-flex align-center flex-wrap">
                            <div>
                                <div class="text-subtitle-1 font-weight-bold">Certidão de Participação</div>
                                <div v-if="certificate.listed" class="text-body-2 grey--text">
                                    Atalho para baixar a certidão emitida pela Administração Judicial.
                                </div>
                                <div v-else class="text-body-2 grey--text">
                                    {{ certificate.message || 'Você não está na lista de credores.' }}
                                </div>
                            </div>
                            <v-spacer />
                            <v-select v-if="certificate.classes.length > 1" class="certificate-class-select mr-md-3 mt-3 mt-md-0"
                                dense outlined hide-details="auto" label="Classe" :items="certificate.classes"
                                item-text="label" return-object v-model="certificate.selected" />
                            <v-btn v-if="!certificate.selected || !certificate.selected.issue_blocked || isReviewRequired(certificate.selected)"
                                color="primary" elevation="0" class="mt-3 mt-md-0"
                                :disabled="!$modules.isOpen('site.credit_certificate.issue') || !certificate.selected || certificate.downloading || (!isReviewRequired(certificate.selected) && (!certificate.canIssue || isCertificateBlocked(certificate.selected)))"
                                @click="downloadCertificate('proceeding')">
                                <v-icon left>mdi-download-outline</v-icon>
                                Baixar certidão
                            </v-btn>
                        </div>
                        <v-alert v-if="certificate.selected && isCertificateBlocked(certificate.selected) && !isReviewRequired(certificate.selected)" type="warning" dense text class="mt-3 mb-0">
                            {{ certificate.selected.issue_block_reason || 'Esta certidão está vencida ou invalidada. O download está bloqueado.' }}
                        </v-alert>
                        <div v-else-if="certificate.selected && certificate.selected.valid_until" class="text-caption grey--text mt-3">
                            Válida até {{ formatDate(certificate.selected.valid_until) }}.
                        </div>
                    </v-card-text>
                    <CertificateReviewRequiredDialog v-model="certificate.reviewRequiredDialog" />
                </v-card>

                <v-alert v-if="dataset.records.length === 0" type="info" outlined class="mb-4">
                    Não há impugnações vinculadas ao seu usuário neste momento.
                </v-alert>

                <v-card v-for="(record, index) in dataset.records" :key="index" outlined class="mb-4">
                    <v-card-title class="d-flex align-center flex-wrap">
                        <div>
                            <div class="text-subtitle-1 font-weight-bold">
                                Processo {{ record.process_number || '-' }}
                            </div>
                            <div class="text-caption grey--text">
                                {{ record.client && record.client.name ? record.client.name : 'RJ123Milhas' }}
                            </div>
                        </div>
                        <v-spacer />
                        <v-chip small :color="getStatusColor(record.status)" text-color="white">
                            {{ getStatusLabel(record.status) }}
                        </v-chip>
                    </v-card-title>

                    <v-divider />

                    <v-card-text>
                        <v-row dense>
                            <v-col cols="12" md="4">
                                <div class="text-caption grey--text">Tipo</div>
                                <div class="font-weight-medium">{{ getProceedingTypeLabel(record.type) }}</div>
                            </v-col>
                            <v-col cols="12" md="4">
                                <div class="text-caption grey--text">Trânsito em julgado</div>
                                <div class="font-weight-medium">{{ record.final_judgment ? 'Sim' : 'Não' }}</div>
                            </v-col>
                            <v-col cols="12" md="4">
                                <div class="text-caption grey--text">Atualizado no QGC</div>
                                <div class="font-weight-medium">{{ record.qgc_updated ? 'Sim' : 'Não' }}</div>
                            </v-col>
                        </v-row>

                        <v-row dense class="mt-2">
                            <v-col cols="12" md="6">
                                <v-card outlined class="pa-3" height="100%">
                                    <div class="text-caption grey--text">Sentença</div>
                                    <div class="font-weight-medium">{{ record.has_sentence ? 'Registrada' : 'Não registrada' }}</div>
                                    <div v-if="record.sentence_result" class="mt-1">{{ record.sentence_result }}</div>
                                    <div v-if="record.sentence_changed_credit" class="mt-2">
                                        <v-chip x-small color="orange" text-color="white">Alterou crédito</v-chip>
                                        <span class="ml-2">{{ formatCurrency(record.sentence_final_value) }}</span>
                                    </div>
                                </v-card>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-card outlined class="pa-3" height="100%">
                                    <div class="text-caption grey--text">Recurso</div>
                                    <div class="font-weight-medium">{{ record.has_appeal ? getAppealResultLabel(record.appeal_result) : 'Não registrado' }}</div>
                                    <div v-if="record.appeal_changed_credit" class="mt-2">
                                        <v-chip x-small color="orange" text-color="white">Alterou crédito</v-chip>
                                        <span class="ml-2">{{ formatCurrency(record.appeal_final_value) }}</span>
                                    </div>
                                </v-card>
                            </v-col>
                        </v-row>

                        <v-expansion-panels flat multiple class="mt-4">
                            <v-expansion-panel>
                                <v-expansion-panel-header>Créditos vinculados</v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <v-list v-if="record.participants && record.participants.length" dense>
                                        <template v-for="(participant, pIndex) in record.participants">
                                            <v-list-item :key="`participant-${index}-${pIndex}`">
                                                <v-list-item-content>
                                                    <v-list-item-title>{{ participant.name || 'Credor' }}</v-list-item-title>
                                                    <v-list-item-subtitle>
                                                        {{ formatRegistration(participant.registration) }}
                                                    </v-list-item-subtitle>
                                                </v-list-item-content>
                                            </v-list-item>
                                            <v-list-item v-for="(credit, cIndex) in participant.credits" :key="`credit-${index}-${pIndex}-${cIndex}`" class="pl-8">
                                                <v-list-item-content>
                                                    <v-list-item-title>{{ formatCurrency(credit.value) }}</v-list-item-title>
                                                    <v-list-item-subtitle>
                                                        {{ getCreditorClassLabel(credit.class) }}
                                                        <span v-if="credit.origin_identifier"> - Origem: {{ credit.origin_identifier }}</span>
                                                    </v-list-item-subtitle>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </template>
                                    </v-list>
                                    <div v-else class="grey--text">Nenhum crédito vinculado para exibição.</div>
                                </v-expansion-panel-content>
                            </v-expansion-panel>

                            <v-expansion-panel>
                                <v-expansion-panel-header>Resultado por crédito</v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <v-list v-if="record.credit_results && record.credit_results.length" dense>
                                        <v-list-item v-for="(result, rIndex) in record.credit_results" :key="`result-${index}-${rIndex}`">
                                            <v-list-item-content>
                                                <v-list-item-title>
                                                    {{ result.stage === 'appeal' ? 'Recurso' : 'Sentença' }}
                                                    <v-chip v-if="result.changed_credit" x-small class="ml-2" color="orange" text-color="white">
                                                        Alterou crédito
                                                    </v-chip>
                                                </v-list-item-title>
                                                <v-list-item-subtitle v-if="result.result_text">
                                                    {{ result.result_text }}
                                                </v-list-item-subtitle>
                                                <v-list-item-subtitle v-if="result.final_value">
                                                    Valor final: {{ formatCurrency(result.final_value) }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-list>
                                    <div v-else class="grey--text">Nenhum resultado específico por crédito foi liberado.</div>
                                </v-expansion-panel-content>
                            </v-expansion-panel>

                            <v-expansion-panel>
                                <v-expansion-panel-header>Decisões e documentos visíveis</v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <v-list v-if="record.decisions && record.decisions.length" dense>
                                        <v-list-item v-for="(decision, dIndex) in record.decisions" :key="`decision-${index}-${dIndex}`">
                                            <v-list-item-content>
                                                <v-list-item-title>{{ decision.title }}</v-list-item-title>
                                                <v-list-item-subtitle>
                                                    {{ getDecisionTypeLabel(decision.decision_type) }}
                                                    <span v-if="decision.reference"> - Ref.: {{ decision.reference }}</span>
                                                    <span v-if="decision.decided_at"> - {{ formatDate(decision.decided_at) }}</span>
                                                </v-list-item-subtitle>
                                                <v-list-item-subtitle v-if="decision.description">
                                                    {{ decision.description }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                            <v-list-item-action v-if="decision.file_url">
                                                <v-btn small outlined color="primary" :href="decision.file_url" target="_blank" rel="noopener noreferrer">
                                                    <v-icon left small>mdi-file-eye-outline</v-icon>
                                                    Abrir
                                                </v-btn>
                                            </v-list-item-action>
                                        </v-list-item>
                                    </v-list>
                                    <div v-else class="grey--text">Nenhuma decisão foi marcada como visível ao credor.</div>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </v-card-text>
                </v-card>
            </template>
        </div>
    </v-container>
</template>

<script>
import axios from 'axios'
import moment from 'moment-timezone'
import { downloadPdfResponse, readBlobError } from '@/common/util/download-file'
import CertificateReviewRequiredDialog from '@/components/creditor/CertificateReviewRequiredDialog.vue'

export default {
    name: 'UserProceedings',
    components: { CertificateReviewRequiredDialog },
    data: () => ({
        certificateFeatureEnabled: true,
        dataset: {
            loading: false,
            records: [],
        },
        certificate: {
            loading: false,
            downloading: false,
            listed: false,
            canIssue: false,
            message: '',
            classes: [],
            selected: null,
            reviewRequiredDialog: false,
        },
        proceedingTypes: [
            { id: 1, name: 'Habilitação retardatária' },
            { id: 2, name: 'Impugnação retardatária' },
            { id: 3, name: 'Impugnação tempestiva' },
            { id: 4, name: 'Classificação de crédito público' },
            { id: 5, name: 'Restituição' },
            { id: 6, name: 'Pretensão' },
        ],
        creditorClasses: [
            { id: 1, name: 'Trabalhistas' },
            { id: 2, name: 'Garantia Real' },
            { id: 3, name: 'Quirografários' },
            { id: 4, name: 'ME/EPP' },
            { id: 5, name: 'Extraconcursais' },
            { id: 6, name: 'Ilíquidos' },
        ],
        appealResults: [
            { id: 1, name: 'Não conhecido' },
            { id: 2, name: 'Conhecido e desprovido' },
            { id: 3, name: 'Conhecido e provido' },
            { id: 4, name: 'Conhecido e provido em parte' },
        ],
        decisionTypes: [
            { id: 'suspension', name: 'Suspensão' },
            { id: 'diligence', name: 'Diligência' },
            { id: 'certificate_request', name: 'Juntada/solicitação de certidão' },
            { id: 'other', name: 'Outra decisão' },
        ],
    }),

    created() {
        // O download da certidão é exclusivo do credor; a aba também é acessada pelo procurador.
        const user = JSON.parse(localStorage.getItem('user') || 'null')
        this.certificateFeatureEnabled = this.certificateFeatureEnabled && !!user && Number(user.profile) === 1

        this.loadData()
        if (this.certificateFeatureEnabled) {
            this.loadCertificateClasses()
        }
    },

    methods: {
        async loadData() {
            this.dataset.loading = true

            try {
                const result = await axios.get('/api/proceedings/my')
                this.dataset.records = result.data.content || []
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000,
                })
            } finally {
                this.dataset.loading = false
            }
        },

        async loadCertificateClasses() {
            this.certificate.loading = true

            try {
                const result = await axios.get('/api/issued-credit-certificates/classes')
                const content = result.data.content || {}
                const classes = (content.classes || []).map((item) => ({
                    ...item,
                    label: item.label || `${item.creditor_type_label} - ${item.amount_formatted}`
                }))

                this.certificate.listed = !!content.listed
                this.certificate.canIssue = !!content.can_issue
                this.certificate.message = content.message || ''
                this.certificate.classes = classes
                this.certificate.selected = classes.length ? classes[0] : null
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000,
                })
            } finally {
                this.certificate.loading = false
            }
        },

        async downloadCertificate(source) {
            if (this.$moduleBlocked('site.credit_certificate.issue')) return
            if (!this.certificate.selected) return
            if (this.isReviewRequired(this.certificate.selected)) {
                this.certificate.reviewRequiredDialog = true
                return
            }

            this.certificate.downloading = true

            try {
                const result = await axios.get('/api/issued-credit-certificates/download', {
                    params: {
                        creditor_type: this.certificate.selected.creditor_type,
                        currency: this.certificate.selected.currency,
                        source,
                    },
                    responseType: 'blob',
                })

                await downloadPdfResponse(
                    result,
                    `certidao_participacao_${this.certificate.selected.auth_code || 'credor'}.pdf`,
                    'Certidão de Participação'
                )
                await this.loadCertificateClasses()
            } catch (error) {
                const blobError = await readBlobError(error)
                if (blobError && blobError.code === 'review_required') {
                    this.certificate.reviewRequiredDialog = true
                    return
                }

                this.$dialog.notify.error(blobError && blobError.message ? blobError.message : this.$codes(error), {
                    position: 'top-right',
                    timeout: 3000,
                })
            } finally {
                this.certificate.downloading = false
            }
        },

        isReviewRequired(item) {
            return !!item && item.issue_block_code === 'review_required'
        },

        isCertificateBlocked(item) {
            if (!item) return true
            if (item.issue_blocked) return true
            if (item.invalidated) return true
            if (!item.valid_until) return false
            return new Date(item.valid_until) < new Date()
        },

        getStatusLabel(status) {
            return Number(status) === 1 ? 'Finalizada' : 'Em andamento'
        },

        getStatusColor(status) {
            return Number(status) === 1 ? 'success' : 'primary'
        },

        getProceedingTypeLabel(id) {
            return this.getLabel(this.proceedingTypes, id)
        },

        getCreditorClassLabel(id) {
            return this.getLabel(this.creditorClasses, id)
        },

        getAppealResultLabel(id) {
            return this.getLabel(this.appealResults, id) || 'Registrado'
        },

        getDecisionTypeLabel(id) {
            return this.getLabel(this.decisionTypes, id)
        },

        getLabel(items, id) {
            const item = items.find((entry) => String(entry.id) === String(id))
            return item ? item.name : '-'
        },

        formatCurrency(value) {
            return Number(value || 0).toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
            })
        },

        formatDate(date) {
            if (!date) return '-'
            return moment(date).format('DD/MM/YYYY')
        },

        formatRegistration(value) {
            const digits = String(value || '').replace(/\D/g, '')
            if (digits.length === 11) {
                return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
            }
            if (digits.length === 14) {
                return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
            }
            return value || '-'
        },
    },
}
</script>

<style scoped>
.certificate-class-select {
    max-width: 420px;
}
</style>
